import * as THREE from 'three';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';

/**
 * Represents a basketball hoop with rim centered at origin (0,0,0)
 * All components are positioned relative to the rim center
 */
export class BasketballHoop {
  private mesh: THREE.Group;
  private backboardDepth: number = BasketballDimensions.backboard.depth;
  private hoop: THREE.Mesh;
  private hoopRadius: number = BasketballDimensions.rim.radius;
  private netLength: number = BasketballDimensions.net.length;
  private netLines: THREE.Line[] = [];
  private netRings: THREE.Line[] = [];
  private isNetAnimating: boolean = false;
  private netAnimationStartTime: number = 0;
  private readonly NET_ANIMATION_DURATION: number = 500; // 0.5 seconds
  private readonly NET_ANIMATION_DELAY: number = 250; // 250ms delay before animation starts
  private readonly NET_WAVE_AMPLITUDE: number = 0.15; // Reduced from 0.3 for less dramatic wave
  private readonly NET_SHORTENING_FACTOR: number = 0.85; // Changed from 0.7 to reduce vertical stretching
  
  constructor(color: number = 0xfd7e14) {
    this.mesh = new THREE.Group();
    this.createHoop(color);
  }
  
  /**
   * Returns the position of the center of the rim in local coordinates
   * @returns THREE.Vector3 representing the center of the rim
   */
  public getRimCenterPosition(): THREE.Vector3 {
    return new THREE.Vector3(0, 0, 0);
  }
  
  /**
   * Returns the position of the center of the rim in world coordinates
   * @returns THREE.Vector3 representing the center of the rim in world space
   */
  public getRimCenterWorldPosition(): THREE.Vector3 {
    const localPosition = this.getRimCenterPosition();
    return this.mesh.localToWorld(localPosition.clone());
  }
  
  /**
   * Positions the entire hoop assembly at the specified world position
   * @param x X coordinate
   * @param y Y coordinate
   * @param z Z coordinate
   */
  public positionAt(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y, z);
  }
  
  /**
   * Returns the THREE.Group containing all hoop components
   * @returns THREE.Group
   */
  public getMesh(): THREE.Group {
    return this.mesh;
  }
  
  private createHoop(color: number): void {
    // RIM - orange ring centered at 0,0,0 (origin)
    const hoopTubeRadius = BasketballDimensions.rim.tubeRadius;
    const hoopGeometry = new THREE.TorusGeometry(this.hoopRadius, hoopTubeRadius, 16, 32);
    const hoopMaterial = new THREE.MeshStandardMaterial({ color: 0xfd7e14 }); // Orange color
    this.hoop = new THREE.Mesh(hoopGeometry, hoopMaterial);
    this.hoop.rotation.x = Math.PI / 2;
    this.hoop.position.set(0, 0, 0); // Rim centered at 0,0,0
    this.hoop.castShadow = true;
    this.mesh.add(this.hoop);
    
    // BACKBOARD - positioned behind the rim
    // Calculate backboard position relative to rim
    const rimDistanceFromBackboard = BasketballDimensions.rim.distanceFromBackboard;
    const backboardZ = -(rimDistanceFromBackboard + this.hoopRadius);
    const backboardY = this.hoop.position.y + BasketballDimensions.backboard.height / 2 - BasketballDimensions.rim.heightAboveBackboardBottom;
    
    // Create backboard frame (four separate edges)
    const borderThickness = 0.05; // 5cm border thickness
    const borderMaterial = new THREE.MeshStandardMaterial({ 
      color: color,
      roughness: 0.1,
      metalness: 0.1
    });

    // Top edge
    const topEdgeGeometry = new THREE.BoxGeometry(
      BasketballDimensions.backboard.width + borderThickness * 2,
      borderThickness,
      this.backboardDepth
    );
    const topEdge = new THREE.Mesh(topEdgeGeometry, borderMaterial);
    topEdge.position.set(0, backboardY + BasketballDimensions.backboard.height / 2 + borderThickness / 2, backboardZ);
    topEdge.castShadow = true;
    this.mesh.add(topEdge);

    // Bottom edge
    const bottomEdgeGeometry = new THREE.BoxGeometry(
      BasketballDimensions.backboard.width + borderThickness * 2,
      borderThickness,
      this.backboardDepth
    );
    const bottomEdge = new THREE.Mesh(bottomEdgeGeometry, borderMaterial);
    bottomEdge.position.set(0, backboardY - BasketballDimensions.backboard.height / 2 - borderThickness / 2, backboardZ);
    bottomEdge.castShadow = true;
    this.mesh.add(bottomEdge);

    // Left edge
    const leftEdgeGeometry = new THREE.BoxGeometry(
      borderThickness,
      BasketballDimensions.backboard.height + borderThickness * 2,
      this.backboardDepth
    );
    const leftEdge = new THREE.Mesh(leftEdgeGeometry, borderMaterial);
    leftEdge.position.set(-BasketballDimensions.backboard.width / 2 - borderThickness / 2, backboardY, backboardZ);
    leftEdge.castShadow = true;
    this.mesh.add(leftEdge);

    // Right edge
    const rightEdgeGeometry = new THREE.BoxGeometry(
      borderThickness,
      BasketballDimensions.backboard.height + borderThickness * 2,
      this.backboardDepth
    );
    const rightEdge = new THREE.Mesh(rightEdgeGeometry, borderMaterial);
    rightEdge.position.set(BasketballDimensions.backboard.width / 2 + borderThickness / 2, backboardY, backboardZ);
    rightEdge.castShadow = true;
    this.mesh.add(rightEdge);
    
    // Main backboard (transparent)
    const backboardGeometry = new THREE.BoxGeometry(
      BasketballDimensions.backboard.width, 
      BasketballDimensions.backboard.height, 
      this.backboardDepth
    );
    const backboardMaterial = new THREE.MeshPhysicalMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: 0.7,
      roughness: 0.1,
      transmission: 0.5 // Makes it transparent/glass-like
    });
    const backboard = new THREE.Mesh(backboardGeometry, backboardMaterial);
    
    // Position backboard with its center at same height as top of rim
    // and proper distance behind the rim
    backboard.position.set(0, backboardY, backboardZ);
    backboard.castShadow = true;
    this.mesh.add(backboard);
    
    // White rectangle marking on backboard
    this.addBackboardRectangle(
      backboard, 
      BasketballDimensions.backboard.rectangleWidth, 
      BasketballDimensions.backboard.rectangleHeight, 
      BasketballDimensions.backboard.rectangleLineWidth
    );
    
    // Add net
    this.addNet(this.hoop, this.hoopRadius, this.netLength);
  }
  
  private addBackboardRectangle(backboard: THREE.Mesh, width: number, height: number, lineWidth: number): void {
    // Create the 2" white rectangle marking on the backboard
    const rectShape = new THREE.Shape();
    rectShape.moveTo(-width / 2, -height / 2);
    rectShape.lineTo(width / 2, -height / 2);
    rectShape.lineTo(width / 2, height / 2);
    rectShape.lineTo(-width / 2, height / 2);
    rectShape.lineTo(-width / 2, -height / 2);
    
    // Create a hole in the shape (inner rectangle)
    const innerWidth = width - lineWidth * 2;
    const innerHeight = height - lineWidth * 2;
    const hole = new THREE.Path();
    hole.moveTo(-innerWidth / 2, -innerHeight / 2);
    hole.lineTo(innerWidth / 2, -innerHeight / 2);
    hole.lineTo(innerWidth / 2, innerHeight / 2);
    hole.lineTo(-innerWidth / 2, innerHeight / 2);
    hole.lineTo(-innerWidth / 2, -innerHeight / 2);
    rectShape.holes.push(hole);
    
    const rectGeometry = new THREE.ShapeGeometry(rectShape);
    const rectMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    const rectangle = new THREE.Mesh(rectGeometry, rectMaterial);
    
    // Position the rectangle at the center of the backboard, slightly in front
    rectangle.position.z = this.backboardDepth / 2 + 0.001;
    
    // Add to backboard
    backboard.add(rectangle);
  }
  
  private addNet(hoop: THREE.Mesh, hoopRadius: number, netLength: number): void {
    // Create a simplified net with lines
    const numSegments = BasketballDimensions.net.segments;
    const numRings = BasketballDimensions.net.rings;
    const material = new THREE.LineBasicMaterial({ color: 0xffffff });
    
    const netGroup = new THREE.Group();

    // Calculate positions for the zigzag pattern
    const ringPositions = [];
    for (let j = 1; j <= numRings; j++) {
      const progress = j / numRings;
      const ringHeight = netLength * progress;
      // Use the same curve calculation for the net shape
      const curveInFactor = Math.sin(progress * Math.PI * 0.5);
      const ringRadius = hoopRadius * (1 - curveInFactor * 0.5);
      ringPositions.push({ height: ringHeight, radius: ringRadius });
    }
    
    // Create zigzag vertical lines
    for (let i = 0; i < numSegments; i++) {
      const angle = (i / numSegments) * Math.PI * 2;
      const nextAngle = ((i + 1) / numSegments) * Math.PI * 2; // Increased offset for more pronounced diamonds
      const startX = Math.cos(angle) * hoopRadius;
      const startY = Math.sin(angle) * hoopRadius;
      
      const points = [];
      // Start from the hoop
      points.push(new THREE.Vector3(startX, startY, 0));
      
      // Add points at each height level with zigzag pattern
      ringPositions.forEach(({ height, radius }, idx) => {
        // Alternate between current angle and next angle to create zigzag
        const useAngle = idx % 2 === 0 ? nextAngle : angle;
        const x = Math.cos(useAngle) * radius;
        const y = Math.sin(useAngle) * radius;
        points.push(new THREE.Vector3(x, y, height));
      });
      
      // End at the bottom of the net
      const bottomRadius = hoopRadius * 0.45;
      const bottomAngle = numRings % 2 === 0 ? angle : nextAngle; // Match last zigzag pattern
      const bottomX = Math.cos(bottomAngle) * bottomRadius;
      const bottomY = Math.sin(bottomAngle) * bottomRadius;
      points.push(new THREE.Vector3(bottomX, bottomY, netLength));
      
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, material);
      this.netLines.push(line);
      netGroup.add(line);

      // Create the connecting zigzag line
      const connectingPoints = [];
      connectingPoints.push(new THREE.Vector3(startX, startY, 0));
      
      // Add zigzag connections between rings
      ringPositions.forEach(({ height, radius }, idx) => {
        const useAngle = idx % 2 === 0 ? angle : nextAngle;
        const x = Math.cos(useAngle) * radius;
        const y = Math.sin(useAngle) * radius;
        connectingPoints.push(new THREE.Vector3(x, y, height));
      });
      
      // Connect to bottom
      const connectingBottomAngle = numRings % 2 === 0 ? nextAngle : angle;
      const connectingBottomX = Math.cos(connectingBottomAngle) * bottomRadius;
      const connectingBottomY = Math.sin(connectingBottomAngle) * bottomRadius;
      connectingPoints.push(new THREE.Vector3(connectingBottomX, connectingBottomY, netLength));
      
      const connectingGeometry = new THREE.BufferGeometry().setFromPoints(connectingPoints);
      const connectingLine = new THREE.Line(connectingGeometry, material);
      this.netLines.push(connectingLine);
      netGroup.add(connectingLine);
    }
    
    // Add the net group to the hoop
    hoop.add(netGroup);
  }

  /**
   * Triggers the net animation when a ball goes through the hoop
   */
  public triggerNetAnimation(): void {
    this.isNetAnimating = true;
    this.netAnimationStartTime = Date.now();
  }

  /**
   * Updates the net animation if it's currently playing
   * @param currentTime The current time in milliseconds
   */
  public updateNetAnimation(currentTime: number): void {
    if (!this.isNetAnimating) return;

    const elapsed = currentTime - this.netAnimationStartTime;
    
    // Don't start animation until delay has passed
    if (elapsed < this.NET_ANIMATION_DELAY) return;
    
    const progress = Math.min((elapsed - this.NET_ANIMATION_DELAY) / this.NET_ANIMATION_DURATION, 1);

    // Easing function for smooth animation
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    const waveProgress = easeOut(progress);
    
    // Calculate wave and shortening with reduced vertical effect
    const waveOffset = Math.sin(progress * Math.PI * 2) * this.NET_WAVE_AMPLITUDE;
    const shortening = 1 - (waveProgress * (1 - this.NET_SHORTENING_FACTOR) * 0.5); // Added 0.5 multiplier to reduce vertical movement

    // Calculate positions with animation
    const ringPositions = [];
    const numRings = BasketballDimensions.net.rings;
    for (let j = 1; j <= numRings; j++) {
      const ringProgress = j / numRings;
      const originalHeight = this.netLength * ringProgress;
      const height = originalHeight * shortening;
      const curveInFactor = Math.sin(ringProgress * Math.PI * 0.5);
      const ringRadius = this.hoopRadius * (1 - curveInFactor * 0.5);
      const animatedRadius = ringRadius * (1 - ((1 - shortening) * 0.3)); // Reduced radius animation
      const ringWaveOffset = waveOffset * Math.pow(ringProgress, 1.5); // More gradual wave effect
      ringPositions.push({ height, radius: animatedRadius, waveOffset: ringWaveOffset });
    }

    // Update vertical lines (both main and connecting zigzag lines)
    const numSegments = BasketballDimensions.net.segments;
    for (let i = 0; i < numSegments; i++) {
      const lineIdx = i * 2; // Each segment now has two lines
      const angle = (i / numSegments) * Math.PI * 2;
      const nextAngle = ((i + 1) / numSegments) * Math.PI * 2;

      // Update main line
      const mainLine = this.netLines[lineIdx];
      const mainPoints = mainLine.geometry.attributes.position.array as Float32Array;
      
      // Top point stays fixed
      const startX = Math.cos(angle) * this.hoopRadius;
      const startY = Math.sin(angle) * this.hoopRadius;
      mainPoints[0] = startX;
      mainPoints[1] = startY;
      mainPoints[2] = 0;
      
      // Update intermediate points with zigzag
      ringPositions.forEach(({ height, radius, waveOffset }, idx) => {
        const pointIndex = (idx + 1) * 3;
        const useAngle = idx % 2 === 0 ? nextAngle : angle;
        mainPoints[pointIndex] = Math.cos(useAngle) * radius;
        mainPoints[pointIndex + 1] = Math.sin(useAngle) * radius;
        mainPoints[pointIndex + 2] = height;
        // Add wave offset to Z coordinate for camera-facing sway
        mainPoints[pointIndex + 2] += waveOffset;
      });
      
      // Update bottom point
      const lastIndex = mainPoints.length - 3;
      const bottomRadius = this.hoopRadius * 0.45 * shortening;
      const bottomAngle = numRings % 2 === 0 ? angle : nextAngle;
      mainPoints[lastIndex] = Math.cos(bottomAngle) * bottomRadius;
      mainPoints[lastIndex + 1] = Math.sin(bottomAngle) * bottomRadius;
      mainPoints[lastIndex + 2] = this.netLength * shortening;
      // Add wave offset to Z coordinate for camera-facing sway
      mainPoints[lastIndex + 2] += waveOffset;
      
      mainLine.geometry.attributes.position.needsUpdate = true;

      // Update connecting line
      const connectingLine = this.netLines[lineIdx + 1];
      const connectingPoints = connectingLine.geometry.attributes.position.array as Float32Array;
      
      // Top point stays fixed
      connectingPoints[0] = startX;
      connectingPoints[1] = startY;
      connectingPoints[2] = 0;
      
      // Update intermediate points with opposite zigzag
      ringPositions.forEach(({ height, radius, waveOffset }, idx) => {
        const pointIndex = (idx + 1) * 3;
        const useAngle = idx % 2 === 0 ? angle : nextAngle;
        connectingPoints[pointIndex] = Math.cos(useAngle) * radius;
        connectingPoints[pointIndex + 1] = Math.sin(useAngle) * radius;
        connectingPoints[pointIndex + 2] = height;
        // Add wave offset to Z coordinate for camera-facing sway
        connectingPoints[pointIndex + 2] += waveOffset;
      });
      
      // Update bottom point
      const connectingLastIndex = connectingPoints.length - 3;
      const connectingBottomAngle = numRings % 2 === 0 ? nextAngle : angle;
      connectingPoints[connectingLastIndex] = Math.cos(connectingBottomAngle) * bottomRadius;
      connectingPoints[connectingLastIndex + 1] = Math.sin(connectingBottomAngle) * bottomRadius;
      connectingPoints[connectingLastIndex + 2] = this.netLength * shortening;
      // Add wave offset to Z coordinate for camera-facing sway
      connectingPoints[connectingLastIndex + 2] += waveOffset;
      
      connectingLine.geometry.attributes.position.needsUpdate = true;
    }

    // End animation when complete
    if (progress >= 1) {
      this.isNetAnimating = false;
      this.resetNet();
    }
  }

  /**
   * Resets the net to its original position
   */
  private resetNet(): void {
    // Calculate original positions
    const ringPositions = [];
    const numRings = BasketballDimensions.net.rings;
    for (let j = 1; j <= numRings; j++) {
      const progress = j / numRings;
      const height = this.netLength * progress;
      const curveInFactor = Math.sin(progress * Math.PI * 0.5);
      const radius = this.hoopRadius * (1 - curveInFactor * 0.5);
      ringPositions.push({ height, radius });
    }

    // Reset vertical lines (both main and connecting zigzag lines)
    const numSegments = BasketballDimensions.net.segments;
    for (let i = 0; i < numSegments; i++) {
      const lineIdx = i * 2;
      const angle = (i / numSegments) * Math.PI * 2;
      const nextAngle = ((i + 1) / numSegments) * Math.PI * 2;

      // Reset main line
      const mainLine = this.netLines[lineIdx];
      const mainPoints = mainLine.geometry.attributes.position.array as Float32Array;
      
      // Reset top point
      const startX = Math.cos(angle) * this.hoopRadius;
      const startY = Math.sin(angle) * this.hoopRadius;
      mainPoints[0] = startX;
      mainPoints[1] = startY;
      mainPoints[2] = 0;
      
      // Reset intermediate points with zigzag
      ringPositions.forEach(({ height, radius }, idx) => {
        const pointIndex = (idx + 1) * 3;
        const useAngle = idx % 2 === 0 ? nextAngle : angle;
        mainPoints[pointIndex] = Math.cos(useAngle) * radius;
        mainPoints[pointIndex + 1] = Math.sin(useAngle) * radius;
        mainPoints[pointIndex + 2] = height;
      });
      
      // Reset bottom point
      const lastIndex = mainPoints.length - 3;
      const bottomRadius = this.hoopRadius * 0.45;
      const bottomAngle = numRings % 2 === 0 ? angle : nextAngle;
      mainPoints[lastIndex] = Math.cos(bottomAngle) * bottomRadius;
      mainPoints[lastIndex + 1] = Math.sin(bottomAngle) * bottomRadius;
      mainPoints[lastIndex + 2] = this.netLength;
      
      mainLine.geometry.attributes.position.needsUpdate = true;

      // Reset connecting line
      const connectingLine = this.netLines[lineIdx + 1];
      const connectingPoints = connectingLine.geometry.attributes.position.array as Float32Array;
      
      // Reset top point
      connectingPoints[0] = startX;
      connectingPoints[1] = startY;
      connectingPoints[2] = 0;
      
      // Reset intermediate points with opposite zigzag
      ringPositions.forEach(({ height, radius }, idx) => {
        const pointIndex = (idx + 1) * 3;
        const useAngle = idx % 2 === 0 ? angle : nextAngle;
        connectingPoints[pointIndex] = Math.cos(useAngle) * radius;
        connectingPoints[pointIndex + 1] = Math.sin(useAngle) * radius;
        connectingPoints[pointIndex + 2] = height;
      });
      
      // Reset bottom point
      const connectingLastIndex = connectingPoints.length - 3;
      const connectingBottomAngle = numRings % 2 === 0 ? nextAngle : angle;
      connectingPoints[connectingLastIndex] = Math.cos(connectingBottomAngle) * bottomRadius;
      connectingPoints[connectingLastIndex + 1] = Math.sin(connectingBottomAngle) * bottomRadius;
      connectingPoints[connectingLastIndex + 2] = this.netLength;
      
      connectingLine.geometry.attributes.position.needsUpdate = true;
    }
  }
} 